import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@shared/lib/supabase';
import { useAuthStore } from '../stores/authStore';

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const checkSession = useAuthStore((state) => state.checkSession);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // URL에서 인증 코드 또는 에러 확인
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const queryParams = new URLSearchParams(window.location.search);

        const errorParam = hashParams.get('error') || queryParams.get('error');
        const errorDescription = hashParams.get('error_description') || queryParams.get('error_description');

        if (errorParam) {
          setError(errorDescription || errorParam);
          return;
        }

        // Supabase가 자동으로 세션을 처리
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          console.error('Session error:', sessionError);
          setError(sessionError.message);
          return;
        }

        if (!session) {
          // 세션이 없으면 다시 로그인 페이지로
          setError('로그인에 실패했습니다. 다시 시도해주세요.');
          return;
        }

        // 앱 자체 세션(쿠키)으로 브릿지 — 네이버 커스텀 플로우와 동일한 세션 체계로 통합
        const bridgeResponse = await fetch('/api/v1/auth/oauth/session', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ accessToken: session.access_token }),
        });

        const bridgeData = await bridgeResponse.json();

        if (!bridgeResponse.ok) {
          setError(bridgeData.error || '로그인 처리에 실패했습니다.');
          return;
        }

        // 스토어 상태를 서버 세션과 동기화
        await checkSession();

        if (!bridgeData.termsAgreed) {
          // 최초 로그인(또는 약관 미동의) → 약관 동의 페이지로
          const params = new URLSearchParams({
            user_id: bridgeData.userId,
            provider: bridgeData.provider,
            name: bridgeData.name || '',
            email: bridgeData.email || '',
          });
          navigate(`/terms-agreement?${params.toString()}`, { replace: true });
        } else {
          navigate('/', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError('인증 처리 중 오류가 발생했습니다.');
      }
    };

    handleCallback();
  }, [navigate, checkSession]);

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-soft p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">로그인 실패</h1>
          <p className="text-gray-500 mb-6">{error}</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="w-full bg-primary hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            다시 로그인하기
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-soft p-8 max-w-md w-full text-center">
        <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
        <h1 className="text-xl font-bold text-gray-900 mb-2">로그인 처리 중</h1>
        <p className="text-gray-500">잠시만 기다려주세요...</p>
      </div>
    </div>
  );
}
